import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  Flex,
  Select,
  Box,
  Button,
  HStack,
  VStack,
  Text,
} from "@chakra-ui/react";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";

export function OfiDateTimeRangePicker({
  start,
  end,
  setStart,
  setEnd,
}: {
  start: Date | null;
  setStart: (date: Date | null) => void;
  end: Date | null;
  setEnd: (date: Date | null) => void;
}) {
  return (
    <VStack align={"left"}>
      <Text size={"xs"}>Starttid</Text>
      <OfiDateTimePicker
        startDate={start}
        endDate={end}
        selectsStart={true}
        selected={start}
        onChange={setStart}
      />
      <Text size={"xs"}>Sluttid</Text>
      <OfiDateTimePicker
        startDate={start}
        endDate={end}
        selectsEnd={true}
        selected={end}
        onChange={setEnd}
      />
    </VStack>
  );
}
export function OfiDateTimePicker({
  selected,
  startDate,
  endDate,
  onChange,
  selectsStart,
  selectsEnd,
  borderWidth,
  borderColor,
  borderStyle,
}: {
  selected: Date | null | undefined;
  startDate: Date | null | undefined;
  endDate?: Date | null;
  onChange: (date: Date | null) => void;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  borderWidth?: string;
  borderColor?: string;
  borderStyle?: string;
}) {
  const [date, setDate] = useState<Date | null>(selected ?? null);
  const [time, setTime] = useState<Date>(selected ?? new Date());

  useEffect(() => {
    if (date) {
      const d = new Date(date);
      d.setHours(time.getHours());
      d.setMinutes(time.getMinutes());
      onChange(d);
    } else {
      onChange(null);
    }
  }, [date, time]);

  return (
    <HStack
      marginBottom={"10px"}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderStyle={borderStyle}
    >
      <Box
        background={"white"}
        borderWidth={"2px"}
        padding={"5px"}
        borderRadius={"10px"}
        width={"fit-content"}
      >
        <DatePicker
          calendarStartDay={1}
          showIcon={endDate == null}
          icon={<CalendarIcon />}
          selected={selected}
          onChange={setDate}
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          startDate={startDate}
          endDate={endDate}
          minDate={selectsEnd ? startDate ?? new Date() : new Date()}
          placeholderText={
            selectsStart
              ? "  Välj startdatum"
              : selectsEnd
              ? "  Välj slutdatum"
              : "  Välj datum"
          }
        />
      </Box>
      {date != null && <OfiTimePicker initialTime={time} onChange={setTime} />}
    </HStack>
  );
}

export function OfiTimePicker(props: {
  initialTime: Date;
  onChange: (date: Date) => void;
}) {
  const [date, setDate] = useState<Date>(props.initialTime);

  const selectedHour = (date: Date) => {
    return date.getHours().toString().padStart(2, "0");
  };

  const selectedMinutes = (date: Date) => {
    return date.getMinutes().toString().padStart(2, "0");
  };

  useEffect(() => {
    props.onChange(date);
  }, [selectedHour(date), selectedMinutes(date)]);

  return (
    <Box>
      <Popover>
        <PopoverTrigger>
          <Button
            variant={"outline"}
            borderWidth={"2px"}
            fontWeight={"normal"}
            background={"white"}
          >
            {`Tid: ${selectedHour(date)}:${selectedMinutes(date)}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          background={"blue.50"}
          shadow={"2xl"}
          width={"fit-content"}
        >
          <PopoverArrow backgroundColor={"blue.50"} />
          <PopoverHeader borderColor={"gray.500"} background={"green.100"}>
            Välj tid
          </PopoverHeader>
          <PopoverBody background={"white"}>
            <Flex>
              <Select
                width={"4xs"}
                background={"white"}
                placeholder="Timmar"
                value={selectedHour(date)}
                onChange={(e) => {
                  if (e.target.value.match(/^\d+$/)) {
                    setDate(
                      (old) =>
                        new Date(
                          new Date(old).setHours(parseInt(e.target.value))
                        )
                    );
                  }
                }}
              >
                {[...Array(24).keys()].map((hour) => (
                  <option key={hour} value={hour.toString().padStart(2, "0")}>
                    {hour.toString().padStart(2, "0")}
                  </option>
                ))}
              </Select>
              <Select
                width={"4xs"}
                background={"white"}
                placeholder="Minuter"
                value={selectedMinutes(date)}
                onChange={(e) => {
                  if (e.target.value.match(/^\d+$/)) {
                    setDate(
                      (old) =>
                        new Date(
                          new Date(old).setMinutes(parseInt(e.target.value))
                        )
                    );
                  }
                }}
              >
                {[...Array(60).keys()].map((minute) => (
                  <option
                    key={minute}
                    value={minute.toString().padStart(2, "0")}
                  >
                    {minute.toString().padStart(2, "0")}
                  </option>
                ))}
              </Select>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}
